import { useCallback, useEffect, useRef } from "react";

export const createSaveQueue = () => {
  let tail = Promise.resolve();
  let pending = 0;
  const queue = (save) => {
    pending += 1;
    const next = tail.catch(() => undefined).then(save);
    tail = next.finally(() => { pending -= 1; });
    // Callers observe errors through their save or flush promise.
    tail.catch(() => undefined);
    return tail;
  };
  queue.flush = () => tail;
  queue.hasPending = () => pending > 0;
  return queue;
};

export const createDebouncedSaveQueue = (delayMs = 400) => {
  const enqueue = createSaveQueue();
  let timer = null;
  let latestSave = null;
  let failedSave = null;
  let waiting = [];

  const run = () => {
    if (!latestSave) return enqueue.flush();

    const save = latestSave;
    const listeners = waiting;
    latestSave = null;
    waiting = [];
    timer = null;

    const result = enqueue(async () => {
      try {
        const value = await save();
        failedSave = null;
        return value;
      } catch (error) {
        failedSave = save;
        throw error;
      }
    });
    result.then(
      (value) => listeners.forEach(({ resolve }) => resolve(value)),
      (error) => listeners.forEach(({ reject }) => reject(error)),
    );
    return result;
  };

  // Coalesced callers must supply the complete state of their save domain.
  const queue = (save) => {
    latestSave = save;
    failedSave = null;
    if (timer) window.clearTimeout(timer);
    waiting.forEach(({ resolve }) => resolve(undefined));
    waiting = [];
    const result = new Promise((resolve, reject) => {
      waiting.push({ resolve, reject });
    });
    timer = window.setTimeout(run, delayMs);
    return result;
  };

  queue.flush = async () => {
    let result;
    do {
      if (timer) window.clearTimeout(timer);
      if (!latestSave && failedSave) latestSave = failedSave;
      result = await run();
    } while (latestSave || enqueue.hasPending());
    return result;
  };
  queue.hasPending = () => !!latestSave || !!failedSave || enqueue.hasPending();
  return queue;
};

export const useQueuedSave = (delayMs = 0) => {
  const queue = useRef(null);
  if (!queue.current) {
    queue.current = delayMs > 0
      ? createDebouncedSaveQueue(delayMs)
      : createSaveQueue();
  }

  useEffect(() => {
    const beforeUnload = (event) => {
      if (!queue.current.hasPending()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      queue.current.flush().catch(() => undefined);
    };
  }, []);

  const save = useCallback((operation) => queue.current(operation), []);
  save.flush = () => queue.current.flush();
  save.hasPending = () => queue.current.hasPending();
  return save;
};
